import Service, { service } from '@ember/service';
import ContactsCache from './contacts-cache';
import PubSub from 'pubsub-js';
import ContactModel from 'donor-elf-web/models/contact';
import { tracked } from '@glimmer/tracking';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import CampaignModel from 'donor-elf-web/models/campaign';
import PledgeModel from 'donor-elf-web/models/pledge';
import CampaignPledgeModel from 'donor-elf-web/models/campaign-pledge';
import CampaignGoalModel from 'donor-elf-web/models/campaign-goal';

const CONTACT_ADDED_TOPIC = 'add-something-contact-added';
const TASK_ADDED_TOPIC = 'add-something-task-added';
const PLEDGE_SAVED_TOPIC = 'add-something-pledge-saved';
const CAMPAIGN_SAVED_TOPIC = 'add-something-campaign-saved';
const CAMPAIGN_GOAL_SAVED_TOPIC = 'add-something-campaign-goal-saved';
const CAMPAIGN_PARENT_CAMPAIGN_CHANGED_TOPIC =
  'add-something--parent-campaign-changed';
const MERGE_CONTACTS_TOPIC = 'add-something-merge-contacts';
const OPEN_MODAL_TOPIC = 'add-something-open';
const CLOSE_MODAL_TOPIC = 'add-something-close';
const CAMPAIGN_DELETED_TOPIC = 'add-something-campaign-deleted';

type OpenArgsType =
  | 'contact'
  | 'task'
  | 'pledge'
  | 'transaction'
  | 'campaign'
  | 'campaignGoal'
  | 'changeParentCampaign'
  | 'deleteCampaign'
  | 'mergeContacts';

type OpenArgsContactOptions = {
  recommendedByContactId: string;
};

type OpenArgsTaskOptions = {
  contactId?: string;
  contactIds?: string[];
  actionLabel?: string;
  hideAddAnother?: boolean;
};

type OpenArgsPledgeOptions = {
  existingPledge?: PledgeModel; // present when editing an existing pledge
  contactId?: string; // if present, will auto-fill the contact field on the new pledge
  amount?: number; // if present, will auto-fill the amount field on the new pledge
  frequency?: number; // if present, will auto-fill the frequency field on the new pledge
  startDate?: Date; // if present, will auto-fill the start date field on the new pledge
  campaignId?: string; // if present, will auto-fill the campaign field on the new pledge
};

type OpenArgsCampaignOptions = {
  existingCampaign?: CampaignModel; // present when editing an existing campaign
  parentCampaignId?: string; // id of the parent campaign id to add the new campaign. disregarded if existingCampaign is passed in
};

type OpenArgsCampaignGoalOptions = {
  campaign: CampaignModel;
  campaignGoal?: CampaignGoalModel;
};

type OpenArgsChangeParentCampaignOptions = {
  campaign: CampaignModel;
};

type OpenArgsDeleteCampaignOptions = {
  campaign: CampaignModel;
  moveToCampaignId?: string;
};

type OpenArgsMergeContactsOptions = {
  contactIds: string[]; // exactly 2 contact ids should be passed in
}

export interface OpenArgs {
  type: OpenArgsType;
  contactOptions?: OpenArgsContactOptions;
  taskOptions?: OpenArgsTaskOptions;
  pledgeOptions?: OpenArgsPledgeOptions;
  campaignOptions?: OpenArgsCampaignOptions;
  campaignGoalOptions?: OpenArgsCampaignGoalOptions;
  changeParentCampaignOptions?: OpenArgsChangeParentCampaignOptions;
  deleteCampaignOptions?: OpenArgsDeleteCampaignOptions;
  mergeContactsOptions?: OpenArgsMergeContactsOptions;
}

export interface CloseArgs {}

export interface ContactAddedArgs {
  contact: ContactModel;
  goingToDetailPage: boolean; // true if the user wants to go to the contact detail page after adding the contact
  addContactToJournal: boolean;
  recommendedByContactId?: string;
}

export interface TaskAddedArgs {
  task: V2TaskModel;
}

export interface PledgeSavedArgs {
  pledge: PledgeModel;
  campaignPledge?: CampaignPledgeModel; // present if the pledge was added to a campaign
  newPledge: boolean; // true if a new pledge was created, false if an existing pledge was updated
  deleted: boolean; // true if the pledge was deleted
}

export interface CampaignSavedArgs {
  campaign: CampaignModel;
  newCampaign: boolean; // true if a new campaign was created, false if an existing campaign was updated
}

export interface CampaignGoalSavedArgs {
  campaign: CampaignModel;
  campaignGoal: CampaignGoalModel;
  newCampaignGoal: boolean; // if new campaign goal was created
}

export interface ParentCampaignChangedArgs {
  campaign: CampaignModel;
  previousParentCampaignId: string | undefined;
}

export interface CampaignDeletedArgs {
  campaign: CampaignModel;
  moveToCampaignId?: string;
}

export interface ContactsMergedArgs {
  contactIdKept: string;
  contactIdMerged: string;
}

export default class AddSomethingModal extends Service {
  @service declare contactsCache: ContactsCache;

  @tracked showGotoDetailPage = true;
  // if true
  // - then the checkbox is shown and defaulted to checked
  // - Goto detail page is not shown
  @tracked showAddContactToJournalCheckbox = false;

  // the contact route can set and unset this value so when a task is added
  // while on a contact detail page, it will default to that contact
  currentContactId?: string;

  open(args: OpenArgs) {
    if (args == null) {
      throw 'args cannot be null';
    }

    PubSub.publish(OPEN_MODAL_TOPIC, args);
  }

  close() {
    PubSub.publish(CLOSE_MODAL_TOPIC, {});
  }

  contactAdded(args: ContactAddedArgs) {
    PubSub.publish(CONTACT_ADDED_TOPIC, args);
  }

  subscribeToContactAdded(
    fn: (message: string, data: ContactAddedArgs) => void,
  ): string {
    return PubSub.subscribe(CONTACT_ADDED_TOPIC, fn);
  }

  taskAdded(args: TaskAddedArgs) {
    PubSub.publish(TASK_ADDED_TOPIC, args);
  }

  subscribeToTaskAdded(
    fn: (message: string, data: TaskAddedArgs) => void,
  ): string {
    return PubSub.subscribe(TASK_ADDED_TOPIC, fn);
  }

  pledgeSaved(args: PledgeSavedArgs) {
    PubSub.publish(PLEDGE_SAVED_TOPIC, args);
  }

  subscribeToPledgeSaved(
    fn: (message: string, data: PledgeSavedArgs) => void,
  ): string {
    return PubSub.subscribe(PLEDGE_SAVED_TOPIC, fn);
  }

  campaignSaved(args: CampaignSavedArgs) {
    PubSub.publish(CAMPAIGN_SAVED_TOPIC, args);
  }

  subscribeToCampaignSaved(
    fn: (message: string, data: CampaignSavedArgs) => void,
  ): string {
    return PubSub.subscribe(CAMPAIGN_SAVED_TOPIC, fn);
  }

  campaignGoalSaved(args: CampaignGoalSavedArgs) {
    PubSub.publish(CAMPAIGN_GOAL_SAVED_TOPIC, args);
  }

  subscribeToCampaignGoalSaved(
    fn: (message: string, data: CampaignGoalSavedArgs) => void,
  ): string {
    return PubSub.subscribe(CAMPAIGN_GOAL_SAVED_TOPIC, fn);
  }

  parentCampaignChanged(args: ParentCampaignChangedArgs) {
    PubSub.publish(CAMPAIGN_PARENT_CAMPAIGN_CHANGED_TOPIC, args);
  }

  subscribeToParentCampaignChanged(
    fn: (message: string, data: ParentCampaignChangedArgs) => void,
  ): string {
    return PubSub.subscribe(CAMPAIGN_PARENT_CAMPAIGN_CHANGED_TOPIC, fn);
  }

  campaignDeleted(args: CampaignDeletedArgs) {
    PubSub.publish(CAMPAIGN_DELETED_TOPIC, args);
  }

  subscribeToCampaignDeleted(
    fn: (message: string, data: CampaignDeletedArgs) => void,
  ): string {
    return PubSub.subscribe(CAMPAIGN_DELETED_TOPIC, fn);
  }

  contactsMerged(args: ContactsMergedArgs) {
    PubSub.publish(MERGE_CONTACTS_TOPIC, args);
  }

  subscribeToContactsMerged(fn: (message: string, data: ContactsMergedArgs) => void): string {
    return PubSub.subscribe(MERGE_CONTACTS_TOPIC, fn);
  }

  subscribeToOpen(fn: (message: string, data: OpenArgs) => void): string {
    return PubSub.subscribe(OPEN_MODAL_TOPIC, fn);
  }

  subscribeToClose(fn: (message: string, data: CloseArgs) => void): string {
    return PubSub.subscribe(CLOSE_MODAL_TOPIC, fn);
  }

  unsubscribeWithToken(token: string) {
    PubSub.unsubscribe(token);
  }
}
